/* eslint-disable max-lines */
/**
 * General type definitions and interfaces used across the application.
 * @module GeneralTypes
 */

import { Invoice } from '@biz-nest/commerce-client';
import { Iaddress, Ibilling, Ifile, IfileMeta, IitemInventory } from '@biz-nest/core-client';
import { IgeneralEvent } from '../utils/eventbus';

/** Represents router navigation functionality */
export interface IrouterNav {

  /** Function to navigate to a route */
  navigate: (...args) => void | Promise<boolean>
}

/** Type for navigation routes - can be a string or tuple */
export type TnavRoute = string | [string];

/** Type for general event callback function */
export type TgeneralEventCallArg = (event: IgeneralEvent | null) => void;

/**
 * Creates an object with the same keys and values from the input object.
 * Useful for creating type-safe control names in forms.
 * @template T - The type of the input object
 * @param obj - The input object
 * @returns An object with the same keys and values as the input object
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const controlNames = <T extends Record<string, any>>(obj: T): Record<keyof T, keyof T> => {
  return Object.keys(obj).reduce((acc, key) => {
    acc[key as keyof T] = key as keyof T;

    return acc;
  }, {} as Record<keyof T, keyof T>);
};

/** Type for identifying different photo types (signature or stamp) */
export type TwhichPhoto = 'signature' | 'stamp';

/** Form interface for email configuration */
export interface ImailForm {

  /** Email address */
  email: string;

  /** Email password */
  password: string;
}

/** Interface for color selection with name and selection state */
export interface IcolorSelect {

  /** Name of the color */
  name: string;

  /** Whether the color is selected */
  selected: boolean;
}

/** Additional form values for item customization */
export interface IitemFormExtraVals {

  /** Custom brand name */
  otherBrand: string;

  /** Selected color */
  color: string;

  /** Custom color name */
  otherColor: string;
}

/** Notification interface for inventory editing */
export interface IeditInvenntoryNotif {

  /** Whether the inventory data is valid */
  valid: boolean;

  /** Inventory data excluding certain fields */
  data: Omit<IitemInventory, 'item' |
    'user' | 'urId' | '_id' | 'createdAt' | 'updatedAt' | 'company' | 'quantityRemaining'>;
}

/** Form interface for job card creation */
export interface IjobCardForm {
  nameUser: string;
  billingUser: string;
  phone: string;
  email: string;
  nameMachine: string;
  model: string;
  serialNo: string;
  reportedIssue: string;
  details: string;
  issueOnFirstLook: string;
  cost: string;
}

/** Interface for location information */
export interface Ilocation {

  /** Location name */
  name: string;

  /** Physical address */
  address: string;

  /** Array of phone numbers */
  phones: string[];

  /** Array of email addresses */
  email: string[];

  /** Optional URL to map location */
  mapUrl?: string;
}

/** Interface for support form */
export interface IsupportForm {

  /** Name of the user */
  name: string;

  /** Email address of the user */
  email: string;

  /** Message from the user */
  message: string;
}

/** Interface for code form */
export interface IcodeForm {

  /** Code value */
  code: string;
}

/** Interface for checkout form */
export interface IcheckoutForm {

  /** Card number */
  cardNumber: string;

  /** CVV code */
  cvv: string;

  /** Expiry date */
  expiryDate: string;

  /** Postal code */
  postalCode: string;
}

/** Interface for tag */
export interface Itag {

  /** Name of the tag */
  name: string;

  /** URL of the tag */
  url: string;

  /** Optional route parameters */
  routeParams?: Record<string, string | number | undefined | boolean>;

  /** Optional transform value */
  transform?: number;
}

/** Interface for review form */
export interface IreviewForm {

  /** Name of the reviewer */
  name: string;

  /** Email address of the reviewer */
  email: string;

  /** Review comment */
  comment: string;
}

/** Interface for track form */
export interface ItrackForm {

  /** Email or phone number of the user */
  emailOrPhoneNumber: string;

  /** Tracking ID */
  trackingId: string;
}

/** Interface for assured components */
export interface IassuredComps {

  /** Icon for the component */
  icon: string;

  /** Description of the component */
  description: string;
}

/** Interface for bottom pay methods */
export interface IbottotmPayMethods {

  /** ID of the payment method */
  id: string;

  /** URL of the payment method */
  url: string;
}

/** Interface for bottom list */
export interface IbottomList {

  /** ID of the list item */
  id: string;

  /** Name of the list item */
  name: string;

  /** URL of the list item */
  url: string;
}

/** Interface for verify form */
export interface IverifyForm {

  /** Verification code */
  verificationCode: string;
}

/** Interface for reset form */
export interface IresetForm {

  /** Verification body */
  verificationCode: string;

  /** New password */
  password: string;

  /** Repeat password */
  repeatpassword: string;
}

/** Interface for verify parameters */
export interface IverifyParam {

  /** Verification code */
  verificationCode: string;

  /** Optional password */
  password?: string;

  /** Optional repeat password */
  repeatpassword?: string;

  /** URL for verification */
  url: string;
}

/** Interface for login form */
export interface IloginForm {

  /** Email or phone number of the user */
  emailOrPhoneNumber: string;

  /** Password of the user */
  password: string;

  /** Remember me days */
  rememberMeDays: number;
}

/** Interface for email phone form */
export interface IemailPhoneForm {

  /** Email or phone number of the user */
  emailOrPhoneNumber: string;
}

/** Interface for sign up form */
export interface IsignUpForm {

  /** Email or phone number of the user */
  emailOrPhoneNumber: string;

  /** Password of the user */
  password: string;

  /** Repeat password */
  repeatpassword: string;
}

/** Interface for crumb */
export interface Icrumb {

  /** Name of the crumb */
  name: string;

  /** Icon of the crumb */
  icon: string;

  /** Optional link for the crumb */
  link?: string;
}

/** Interface for chat notify */
export interface IchatNotify {

  /** Type of the notification */
  type: string;

  /** Data for the notification */
  data;
}

/** Interface for form */
export interface Iform {

  /** Amount value */
  amount: string;
}

/** Interface for user common data form */
export interface IuserCommonDataForm {

  /** First name of the user */
  firstName: string;

  /** Last name of the user */
  lastName: string;

  /** Company name of the user */
  companyName: string;

  /** Salutation of the user */
  salutation: string;

  /** Extra company details */
  extraCompanyDetails: string;

  /** Email address of the user */
  email?: string;

  /** Phone number of the user */
  phone?: string;

  /** Array of addresses */
  address: Iaddress[];

  /** Array of billing information */
  billing: Ibilling[];

  /** Permissions object */
  permissions: { [x: string]: boolean; }
}

/** Interface for user form notify */
export interface IuserFormNotify {

  /** Type of the notification */
  type: string;

  /** Whether the data is valid */
  valid: boolean;

  /** Error message */
  error: string;

  /** User data */
  userData: Partial<IuserCommonDataForm>;

  /** Optional array of files */
  files?: Ifile[];
}

/** Type for identifying the component that triggered the form submission */
export type TfromComp = 'customer' | 'profile' | 'staff' | 'deliveryManager' | 'vendorManager' | 'company';

/** Interface for photo deletable */
export interface Iphotodeletable extends IfileMeta {

  /** Whether the photo is being deleted */
  isDeleting: boolean;
}

/** Interface for address form values */
export interface IaddressFormVals {

  /** First name of the address */
  addrFname: string;

  /** Last name of the address */
  addrLname: string;

  /** Address line 1 */
  addrAddressLine1: string;

  /** Address line 2 */
  addrAddressLine2: string;

  /** Country of the address */
  addrCountry: string;

  /** State of the address */
  addrState: string;

  /** City of the address */
  addrCity: string;

  /** Zip code of the address */
  addrZipcode: string;

  /** Phone number of the address */
  addrPhoneNumber: string;

  /** Postal code of the address */
  addrPostalCode: string;
}

/** Interface for edited invoice notify */
export interface IeditedInvoiceNotify {

  /** Type of the notification */
  type?: string;

  /** Array of invoices */
  invoices?: Invoice[];

  /** Total number of invoices */
  invoicesTotal?: number;

  /** Number of paid invoices */
  invoicePaidCount?: number;

  /** Number of unpaid invoices */
  invoiceUnpaidCount?: number;

  /** Number of overdue invoices */
  invoiceOverDueCount?: number;

  /** Total amount of paid invoices */
  invoicePaidTotal?: number;

  /** Total amount of unpaid invoices */
  invoiceUnpaidTotal?: number;

  /** Total amount of overdue invoices */
  invoiceOverDueTotal?: number;

  /** Whether the data is loading */
  loading?: boolean;

  /** Count of invoices */
  count?: number;
}

/** Interface for emit name changes */
export interface IemitNameChanges {

  /** Type of the change */
  type: string;

  /** New value */
  value: string;
}

/** Interface for paginator */
export interface Ipaginator {

  /** Offset value */
  offset: number;

  /** Limit value */
  limit: number;

  /** Optional current index */
  currentIndex?: number;
}

/** Interface for paginator index */
export interface IpaginatorIndex {

  /** List index */
  listIndex: number;

  /** Item index */
  itemIndex: number;
}

/** Interface for password change form */
export interface IpassChangeForm {

  /** Old password */
  oldPassword: string;

  /** New password */
  password: string;

  /** Repeat password */
  repeatpassword: string;
}

/** Interface for tooltip navigation pops */
export interface ItooltipNavPops {

  /** Whether the button is enabled */
  button: boolean;

  /** Text for the tooltip */
  text: string;

  /** Route for the tooltip */
  route: string;
}

/** Interface for tax form */
export interface ItaxForm {

  /** Name of the tax */
  taxName: string;

  /** Percentage of the tax */
  taxPercentage: string;

  /** Status of the tax */
  status: string;
}

/** Interface for component event */
export interface IcomponentEvent {

  /** Type of the event */
  type: string;

  /** Data for the event */
  data;
}

/** Interface for server addresses */
export interface IserverAddresses {

  /** Authentication server URL */
  authServer: string;

  /** Notification server URL */
  notifServer: string;

  /** Commerce server URL */
  commerceServer: string;

  /** Chat server URL */
  chatServer: string;

  /** Manager server URL */
  managerServer: string;

  /** Web metrics server URL */
  webMetricsServer: string;

  /** Mail metrics server URL */
  mailMetricsServer: string;

  /** DNS server URL */
  dnsServer: string;

  /** Mail server URL */
  mailServer: string;
}
